import React from 'react';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import {
  apiLoginRequest,
  backendMsalConfig,
  clearLogoutFlag,
  getDynamicLoginRequest,
  graphLoginRequest,
  guestMsalConfig,
  msalConfig,
  shouldAttemptSilentAuth
} from '@/authConfig.ts';
import { isHeiDomain } from 'app/utils/heiDomain';
import { authTelemetry } from '@/app/services/authTelemetry';

const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';

/**
 * Flag set by the redirect-return handler in index.tsx when Microsoft reports
 * AADSTS650052 (the backend app still needs admin consent), so the login page
 * can render the consent state after the round-trip reloads the app.
 */
export const NEEDS_CONSENT_FLAG_KEY = 'sklls.login.needsConsent';

/**
 * Removes a stale MSAL interaction flag left behind by an abandoned login
 * attempt (e.g. the user navigated away mid-redirect). The status lives in
 * sessionStorage (temporary cache default), is mirrored to a same-named cookie
 * because `storeAuthStateInCookie` is on (MSAL reads the cookie first), and
 * localStorage is a legacy fallback read path — so all three must go.
 */
export const clearStaleMsalInteraction = () => {
  sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
  localStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
  document.cookie = `${MSAL_INTERACTION_STATUS_KEY}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

/**
 * Same-origin guard for the stored post-login deep link: localStorage is
 * writable by any code on the page, so never feed it to window.location
 * unvalidated. Returns null for absolute/protocol-relative URLs pointing
 * elsewhere or anything unparseable.
 */
export const sanitizeRedirectPath = (path: string | null): string | null => {
  if (!path) {
    return null;
  }
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
};

export const getPostLoginRedirectPath = (): string => {
  let redirectPath = sanitizeRedirectPath(localStorage.getItem('postLoginRedirectPath')) || '/';
  if (isHeiDomain(window.location.hostname)) {
    redirectPath = '/translator';
  }
  localStorage.removeItem('postLoginRedirectPath');
  return redirectPath;
};

const readNeedsConsentFlag = (): boolean => {
  const needsConsent = sessionStorage.getItem(NEEDS_CONSENT_FLAG_KEY) === '1';
  sessionStorage.removeItem(NEEDS_CONSENT_FLAG_KEY);
  return needsConsent;
};

interface UseLoginOptions {
  instance: IPublicClientApplication;
  /** Sign in against the Sklls tenant (guest config) instead of the multi-tenant authority. */
  guest: boolean;
  /** Overridable for tests; defaults to a full page navigation. */
  navigate?: (path: string) => void;
}

/**
 * Sign-in flow for the unauthenticated app: silent token acquisition for
 * returning users, otherwise a full-page `loginRedirect` to Microsoft. The
 * redirect flow (unlike the previous popup) cannot strand the user behind a
 * hidden window — on any pre-navigation failure `redirecting` resets and the
 * button is immediately usable again.
 */
export const useLogin = ({ instance, guest, navigate = (path) => (window.location.href = path) }: UseLoginOptions) => {
  const [redirecting, setRedirecting] = React.useState(false);
  const [needsBackendConsent, setNeedsBackendConsent] = React.useState(readNeedsConsentFlag);

  const authority = guest ? guestMsalConfig.auth.authority : msalConfig.auth.authority;

  const signIn = async () => {
    // For returning users, try silent authentication first
    if (shouldAttemptSilentAuth()) {
      try {
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          const silentResponse = await instance.acquireTokenSilent({
            ...apiLoginRequest,
            account,
            authority
          });

          if (silentResponse && account) {
            authTelemetry.trackSessionStart(account, 'silent');
          }
          clearLogoutFlag();
          navigate(getPostLoginRedirectPath());
          return;
        }
      } catch (silentError) {
        authTelemetry.trackSilentAuthFailure(silentError, 'redirect');
      }
    }

    setRedirecting(true);

    const accounts = instance.getAllAccounts();
    const dynamicRequest = getDynamicLoginRequest(accounts.length, accounts[0]);
    authTelemetry.trackInteractiveAuth('redirect', shouldAttemptSilentAuth() ? 'silent_failed' : 'user_initiated');

    // The request-level redirectUri ('/blank.html', meant for popups and
    // silent iframes) must not be used for a whole-tab redirect — return to
    // the app root, where handleRedirectPromise picks up the response.
    const request = { ...dynamicRequest, redirectUri: '/', authority };
    try {
      await instance.loginRedirect(request);
      // On success the browser navigates away and this promise never settles.
    } catch (e) {
      if ((e as { errorCode?: string })?.errorCode === 'interaction_in_progress') {
        // An abandoned earlier attempt left MSAL's interaction flag behind.
        // Clear it only on this detected failure (not pre-emptively, which
        // could clobber a login legitimately running in another tab) and
        // retry once.
        clearStaleMsalInteraction();
        try {
          await instance.loginRedirect(request);
          return;
        } catch {
          // fall through to re-enable the button
        }
      }
      // Failed before navigating (config error, stale state, …) — re-enable
      // the button so the user can simply try again.
      setRedirecting(false);
    }
  };

  const signInAndConsent = async () => {
    const useExternalInstance =
      window.location.search === '?external=true' || window.location.pathname === '/loginExternal';
    const backendInstance = new PublicClientApplication(
      useExternalInstance
        ? backendMsalConfig
        : { auth: { ...backendMsalConfig.auth, authority: guestMsalConfig.auth.authority } }
    );
    try {
      await backendInstance.loginPopup(graphLoginRequest);
      navigate(getPostLoginRedirectPath());
    } catch (_e) {
      // Error handling could be added here if needed
    }
  };

  return { redirecting, needsBackendConsent, setNeedsBackendConsent, signIn, signInAndConsent };
};
